import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useEditorMode } from '../EditorModeContext';
import { useMapEditor } from '../MapEditorContext';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import { START_ROOM_KEY } from '../mapEditorUnreal';
import SegmentPicker, { type SegmentPickerPurpose } from './SegmentPicker';

// Which row the picker was opened for. `append` has no row yet; the other two name one, and the
// entrance names it by the key it stands under in Unreal (START_ROOM_KEY) rather than by a second
// vocabulary for "the first row".
interface PickerTarget {
  purpose: SegmentPickerPurpose;
  roomKey: string;
}

export interface SegmentPickerControls {
  // Adds a segment at the end of the walk.
  openForAppend: () => void;
  // Swaps the module standing at one segment, keeping the placement. Pass START_ROOM_KEY for the
  // entrance, which opens the entrance picker instead — the tabs it may offer are different, because
  // a purpose-built shell can be an entrance and cannot be a link in the chain.
  openForSegment: (roomKey: string) => void;
}

const SegmentPickerContext = createContext<SegmentPickerControls | null>(null);

/**
 * Owns the segment picker, and is the one place a pick is turned into an edit.
 *
 * Both surfaces that open it — the walk in the Scene tree and the segment panel on the right — reach
 * it through this rather than mounting a dialog each. Not for tidiness: committing means three
 * different things (append and select the new row, replace at an index and keep the placement, set the
 * entrance) and every one of them also moves the selection onto what was just chosen. Two copies of
 * that is two chances for one of them to forget the selection, which is the state that reads as "did
 * that actually work?".
 *
 * @param props.children - The map-editor surfaces.
 */
export const SegmentPickerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { rooms, appendRoom, replaceRoomAt, setStartElement, startElement } =
    useMapEditor();
  const { selectMapSegment } = useEditorMode();
  const { elements: catalogElements } = useMapEditorCatalog();

  const [target, setTarget] = useState<PickerTarget | null>(null);

  const openForAppend = useCallback(() => {
    setTarget({ purpose: 'append', roomKey: '' });
  }, []);

  const openForSegment = useCallback((roomKey: string) => {
    if (!roomKey) {
      return;
    }
    setTarget({
      purpose: roomKey === START_ROOM_KEY ? 'entrance' : 'replace',
      roomKey,
    });
  }, []);

  const handleClose = useCallback(() => {
    setTarget(null);
  }, []);

  // What the dialog opens on. For a replace it is the module already standing there, so **Change**
  // lands on that module's own card; for the entrance it is whichever lobby the layout currently
  // opens in, which is empty for one that opens on the map's baked entrance.
  const seedElementId = useMemo(() => {
    if (!target) {
      return '';
    }
    if (target.purpose === 'entrance') {
      return startElement;
    }

    return rooms.find((room) => room.key === target.roomKey)?.id ?? '';
  }, [target, rooms, startElement]);

  const handleCommit = useCallback(
    (elementId: string) => {
      if (!target) {
        return;
      }

      if (target.purpose === 'entrance') {
        setStartElement(elementId);
        selectMapSegment(START_ROOM_KEY);
        return;
      }

      if (target.purpose === 'append') {
        // Selected on the key the add itself minted. A segment that lands unselected leaves the panel
        // describing whatever was chosen before it, which is what made adding read as having failed.
        selectMapSegment(appendRoom(elementId));
        return;
      }

      const index = rooms.findIndex((room) => room.key === target.roomKey);
      if (index < 0) {
        return;
      }
      replaceRoomAt(index, elementId);
      // The placement survives a replace, so the row the author was working on is still the row to be
      // looking at — and it already is, since Change is only reachable from it.
      selectMapSegment(target.roomKey);
    },
    [
      target,
      rooms,
      appendRoom,
      replaceRoomAt,
      setStartElement,
      selectMapSegment,
    ],
  );

  // Goes back to the entrance the host map bakes in. A real answer to the question rather than a
  // reset: it is the only way to undo having chosen a lobby.
  const handleUseMapEntrance = useCallback(() => {
    setStartElement('');
    selectMapSegment(START_ROOM_KEY);
  }, [setStartElement, selectMapSegment]);

  const controls = useMemo<SegmentPickerControls>(
    () => ({ openForAppend, openForSegment }),
    [openForAppend, openForSegment],
  );

  return (
    <SegmentPickerContext.Provider value={controls}>
      {children}
      <SegmentPicker
        open={!!target}
        purpose={target?.purpose ?? 'append'}
        elements={catalogElements}
        seedElementId={seedElementId}
        onUseMapEntrance={
          target?.purpose === 'entrance' ? handleUseMapEntrance : undefined
        }
        onClose={handleClose}
        onCommit={handleCommit}
      />
    </SegmentPickerContext.Provider>
  );
};

/**
 * Opens the segment picker.
 *
 * @returns The two ways in. Both no-op outside the provider, so a surface rendered without it degrades
 * to a dead control rather than throwing inside a render — the map editor is one focus of an overlay
 * that mounts a lot of things.
 */
export const useSegmentPicker = (): SegmentPickerControls => {
  const controls = useContext(SegmentPickerContext);

  return (
    controls ?? {
      openForAppend: () => undefined,
      openForSegment: () => undefined,
    }
  );
};
